// src/components/SignUp.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../styles/signup.css";
import perro from "../assets/perro_cc.webp";
import { X, Eye, EyeOff } from "lucide-react";
import useBackend from "../hooks/useBackend";
import config from "../config";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

// =========================================================
// 🔧 REGISTRO CORTO PETFAM
// Campos visibles: nombre completo, correo, celular,
// contraseña y confirmar contraseña.
// El país se detecta por IP para decidir la pasarela.
// =========================================================
const DEFAULT_COUNTRY = "CO";
const MIN_PASSWORD_LENGTH = 8;

// ✅ Códigos válidos de aliados
const VALID_INFLUENCER_CODES = new Set([
  "LARY",
  "LUISANOMADA",
  "AFGF",
  "SGF1",
  "CHIGUIRO",
  "HUESOS",
  "RG6",
  "PANCHAUX",
  "LAUHOLAHOLA",
  "VANE08",
  "DANNAFAM",
  "JUANPAPET",
  "MELIPETFAM",
  "SILENY",
]);

function normalizeInfluencerCode(raw) {
  return String(raw || "").trim().toUpperCase().replace(/\s+/g, "");
}

function getInfluencerCodeFromUrl(search) {
  const params = new URLSearchParams(search || "");
  const raw = params.get("ref") || params.get("codigo") || params.get("code") || "";
  const normalized = normalizeInfluencerCode(raw);
  return VALID_INFLUENCER_CODES.has(normalized) ? normalized : "";
}

async function fetchFirstOkJson(urls, options) {
  for (const u of urls) {
    try {
      const r = await fetch(u, options);
      if (!r.ok) continue;
      const data = await r.json().catch(() => null);
      return { ok: true, url: u, data };
    } catch {}
  }
  return { ok: false };
}

const COMMON_EMAIL_DOMAINS = [
  "gmail.com",
  "hotmail.com",
  "outlook.com",
  "live.com",
  "icloud.com",
  "yahoo.com",
  "msn.com",
  "hotmail.es",
  "outlook.es",
];

const EMAIL_DOMAIN_FIXES = {
  "gmal.com": "gmail.com",
  "gmial.com": "gmail.com",
  "gmai.com": "gmail.com",
  "gmail.co": "gmail.com",
  "gmail.con": "gmail.com",
  "gmail.cm": "gmail.com",
  "hotmai.com": "hotmail.com",
  "hotmial.com": "hotmail.com",
  "hotmal.com": "hotmail.com",
  "hotmail.co": "hotmail.com",
  "hotmail.con": "hotmail.com",
  "hotnail.com": "hotmail.com",
  "outlok.com": "outlook.com",
  "outloo.com": "outlook.com",
  "otulook.com": "outlook.com",
  "outlook.co": "outlook.com",
  "outlook.con": "outlook.com",
  "yaho.com": "yahoo.com",
  "yahoo.co": "yahoo.com",
  "yahoo.con": "yahoo.com",
  "iclod.com": "icloud.com",
  "icloud.co": "icloud.com",
  "icloud.con": "icloud.com",
  "live.co": "live.com",
  "live.con": "live.com",
  "msn.co": "msn.com",
  "msn.con": "msn.com",
  "hotmail.com.co": "hotmail.com",
  "gmail.com.co": "gmail.com",
  "outlook.com.co": "outlook.com",
};

function levenshtein(a, b) {
  const s = String(a || "");
  const t = String(b || "");
  const rows = s.length + 1;
  const cols = t.length + 1;
  const dp = Array.from({ length: rows }, () => Array(cols).fill(0));
  for (let i = 0; i < rows; i++) dp[i][0] = i;
  for (let j = 0; j < cols; j++) dp[0][j] = j;
  for (let i = 1; i < rows; i++) {
    for (let j = 1; j < cols; j++) {
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[s.length][t.length];
}

function normalizeEmailInput(value) {
  return String(value || "").trim().toLowerCase();
}

function getEmailSuggestion(email) {
  const normalized = normalizeEmailInput(email);
  if (!normalized || !normalized.includes("@")) return "";
  const parts = normalized.split("@");
  if (parts.length !== 2) return "";
  const localPart = parts[0];
  const domain = parts[1];
  if (!localPart || !domain) return "";

  if (EMAIL_DOMAIN_FIXES[domain]) return `${localPart}@${EMAIL_DOMAIN_FIXES[domain]}`;

  let bestMatch = "";
  let bestDistance = Infinity;
  for (const candidate of COMMON_EMAIL_DOMAINS) {
    const dist = levenshtein(domain, candidate);
    if (dist < bestDistance) {
      bestDistance = dist;
      bestMatch = candidate;
    }
  }
  if (bestMatch && bestDistance > 0 && bestDistance <= 2) return `${localPart}@${bestMatch}`;
  return "";
}

export default function SignUp() {
  const location = useLocation();
  const navigate = useNavigate();
  const { post, loading } = useBackend();

  const t = location.state?.transition;
  const screenUp = t === "screen" || t === "screen-up" || t === "up";
  const screenDown = t === "screen-down" || t === "down";

  const [enterClass, setEnterClass] = useState(
    screenDown ? "is-entering-down" : screenUp ? "is-entering" : ""
  );

  useEffect(() => {
    if (screenUp || screenDown) {
      const id = setTimeout(() => setEnterClass(""), 420);
      return () => clearTimeout(id);
    }
  }, [screenUp, screenDown]);

  useEffect(() => {
    const from = location.state?.from;
    const backTo = location.state?.backTo;
    if (typeof backTo === "string" && backTo.trim()) {
      sessionStorage.setItem("pf_signup_from", backTo.trim());
      return;
    }
    if (typeof from === "string" && from.trim()) {
      sessionStorage.setItem("pf_signup_from", from.trim());
    }
  }, [location.state]);

  // ✅ Código de aliado desde la URL
  useEffect(() => {
    const refCode = getInfluencerCodeFromUrl(location.search);
    if (refCode) {
      localStorage.setItem("petfam_influencer_code", refCode);
      sessionStorage.setItem("petfam_influencer_code", refCode);
      return;
    }
    const stored = normalizeInfluencerCode(
      localStorage.getItem("petfam_influencer_code") ||
        sessionStorage.getItem("petfam_influencer_code") ||
        ""
    );
    if (stored && !VALID_INFLUENCER_CODES.has(stored)) {
      localStorage.removeItem("petfam_influencer_code");
      sessionStorage.removeItem("petfam_influencer_code");
    }
  }, [location.search]);

  const influencerCode = useMemo(() => {
    const fromUrl = getInfluencerCodeFromUrl(location.search);
    if (fromUrl) return fromUrl;
    const stored = normalizeInfluencerCode(
      localStorage.getItem("petfam_influencer_code") ||
        sessionStorage.getItem("petfam_influencer_code") ||
        ""
    );
    return VALID_INFLUENCER_CODES.has(stored) ? stored : "";
  }, [location.search]);

  const [formData, setFormData] = useState({
    nombreCompleto: "",
    email: "",
    telefono: "",
    password: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [showSubmitting, setShowSubmitting] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  // 🌎 País SOLO por IP, sin preguntar nada. Decide la pasarela de pago.
  const [country, setCountry] = useState(DEFAULT_COUNTRY);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const urls = [`${API_BASE_URL}/geo/country`, `${API_BASE_URL}/api/geo/country`];
      const res = await fetchFirstOkJson(urls, { cache: "no-store" });
      if (cancelled) return;
      const c = String(res?.data?.country || "").toUpperCase();
      setCountry(c === "EC" ? "EC" : DEFAULT_COUNTRY);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const normalizeNameInput = (val) =>
    String(val ?? "")
      .toUpperCase()
      .replace(/[^A-ZÁÉÍÓÚÜÑ ]/g, "")
      .replace(/\s{2,}/g, " ");

  const emailSuggestion = useMemo(() => {
    const raw = formData.email;
    if (!raw || !raw.includes("@")) return "";
    const suggestion = getEmailSuggestion(raw);
    const normalized = normalizeEmailInput(raw);
    return suggestion && suggestion !== normalized ? suggestion : "";
  }, [formData.email]);

  const validateField = (name, value, data) => {
    const v = String(value ?? "").trim();
    const d = data || formData;

    if (!v) return "Este campo es obligatorio.";

    if (name === "nombreCompleto") {
      if (v.length < 5) return "Escribe tu nombre completo.";
      if (!/^[A-ZÁÉÍÓÚÜÑ ]+$/.test(v)) return "Este campo sólo permite letras.";
      if (v.split(/\s+/).filter(Boolean).length < 2) return "Escribe nombre y apellido.";
      return "";
    }

    if (name === "email") {
      if (!/^\S+@\S+\.\S+$/.test(normalizeEmailInput(v))) return "Usa el formato correo@ejemplo.com.";
      return "";
    }

    if (name === "telefono") {
      const digits = v.replace(/\D/g, "");
      if (country === "CO" && digits.length !== 10) return "El celular debe tener 10 dígitos.";
      if (country === "EC" && !(digits.length === 9 || digits.length === 10)) {
        return "El celular debe tener 9 o 10 dígitos.";
      }
      if (country !== "CO" && country !== "EC" && digits.length < 7) return "Celular inválido.";
      return "";
    }

    if (name === "password") {
      if (v.length < MIN_PASSWORD_LENGTH) return `Debe tener mínimo ${MIN_PASSWORD_LENGTH} caracteres.`;
      return "";
    }

    if (name === "confirmPassword") {
      if (v !== String(d.password || "")) return "Las contraseñas no coinciden.";
      return "";
    }

    return "";
  };

  const validateForm = (data) => {
    const fields = ["nombreCompleto", "email", "telefono", "password", "confirmPassword"];
    const newErrors = {};
    fields.forEach((f) => {
      const msg = validateField(f, data[f], data);
      if (msg) newErrors[f] = msg;
    });
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    setFormData((prevState) => {
      let nextValue = value;

      if (name === "nombreCompleto") nextValue = normalizeNameInput(nextValue);
      if (name === "email") nextValue = String(nextValue || "").replace(/\s+/g, "").toLowerCase();
      if (name === "telefono") nextValue = String(nextValue || "").replace(/\D/g, "").slice(0, 10);

      const nextData = { ...prevState, [name]: nextValue };
      setErrors((prev) => ({ ...prev, [name]: validateField(name, nextValue, nextData) }));

      // Si cambia la contraseña, revalida la confirmación
      if (name === "password" && nextData.confirmPassword) {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: validateField("confirmPassword", nextData.confirmPassword, nextData),
        }));
      }

      return nextData;
    });

    setSubmitError("");
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setFormData((prev) => {
      setErrors((p) => ({ ...p, [name]: validateField(name, prev[name], prev) }));
      return prev;
    });
  };

  const applyEmailSuggestion = () => {
    if (!emailSuggestion) return;
    setFormData((prev) => ({ ...prev, email: emailSuggestion }));
    setTouched((prev) => ({ ...prev, email: true }));
    setErrors((prev) => ({ ...prev, email: "" }));
    setSubmitError("");
  };

  const handleClose = (e) => {
    e.preventDefault();
    const explicit = (location.state?.backTo || "").trim().toLowerCase();
    if (explicit === "/servicios") {
      navigate("/servicios", { state: { transition: "down" } });
      return;
    }
    if (explicit === "/" || explicit === "/home") {
      navigate("/", { state: { transition: "down" } });
      return;
    }
    const fromState = (location.state?.from || "").trim().toLowerCase();
    const fromStored = (sessionStorage.getItem("pf_signup_from") || "").trim().toLowerCase();
    const from = fromState || fromStored;
    if (from === "servicios" || from === "/servicios") {
      navigate("/servicios", { state: { transition: "down" } });
    } else {
      navigate("/", { state: { transition: "down" } });
    }
  };

  const SIGNUP_ENDPOINT = "/user/signup";

  // Guarda la sesión igual que Login.jsx, para que la persona siga el flujo
  // sin volver a escribir su correo y contraseña.
  const persistSession = (data, email) => {
    const newId = data?.idUser || data?.id || data?.userId;
    const token = data?.token || data?.jwt || data?.accessToken || null;
    if (!newId || !token) return false;

    localStorage.setItem("token", token);
    localStorage.setItem("petfamUser", JSON.stringify(data));
    localStorage.setItem("idUser", String(newId));
    localStorage.setItem("userEmail", data?.email || email);

    const rawRoles = Array.isArray(data.roles) ? data.roles : [];
    localStorage.setItem("userRoles", JSON.stringify(rawRoles));
    if (rawRoles.includes("ROLE_ADMIN")) localStorage.setItem("userRole", "ADMIN");
    else if (rawRoles.includes("ROLE_CLIENTE")) localStorage.setItem("userRole", "CLIENTE");

    return true;
  };

  const handleRegistrarme = async () => {
    setTouched({
      nombreCompleto: true,
      email: true,
      telefono: true,
      password: true,
      confirmPassword: true,
    });

    const validationErrors = validateForm(formData);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSubmitError("");
    setShowSubmitting(true);

    const email = formData.email.trim().toLowerCase();
    const password = formData.password;
    const fullNameParts = formData.nombreCompleto.trim().split(/\s+/).filter(Boolean);
    const firstName = fullNameParts.shift() || "";
    const lastName = fullNameParts.join(" ");

    const payload = {
      name: firstName,
      lastName,
      phone: formData.telefono.trim(),
      email,
      password,
      country,
    };

    try {
      await post(SIGNUP_ENDPOINT, null, payload);

      localStorage.setItem("petfam_customer_email", email);
      localStorage.setItem("userEmail", email);
      if (influencerCode) {
        localStorage.setItem("petfam_influencer_code", influencerCode);
        sessionStorage.setItem("petfam_influencer_code", influencerCode);
      }
      sessionStorage.setItem("pf_signup_from", "signup");

      // Entra directo con la contraseña recién creada: registro → mascota.
      const loginResp = await post("/auth/login", "", { email, password });

      if (loginResp?.status === 200 && loginResp?.data && persistSession(loginResp.data, email)) {
        setShowSubmitting(false);
        navigate("/welcomepet", { state: { from: "signup", transition: "up" }, replace: true });
        return;
      }

      // Si el login automático no pudo entrar, la persona entra a mano.
      setShowSubmitting(false);
      navigate("/login", {
        state: { transition: "up", from: "/crear-cuenta", prefillEmail: email },
      });
    } catch (e) {
      setSubmitError(e?.message || "Ocurrió un error creando el usuario.");
      setShowSubmitting(false);
    }
  };

  const showError = (field) => touched[field] && errors[field];

  return (
    <div className={`dd-shell ${enterClass}`}>
      {showSubmitting ? (
        <div className="pf-modal-overlay" role="dialog" aria-modal="true" aria-label="Registrándome">
          <div className="pf-modal">
            <p className="pf-modal-title">Creando tu cuenta...</p>
            <p className="pf-modal-subtitle">Por favor espera un momento.</p>
          </div>
        </div>
      ) : null}

      <a
        href="#close"
        onClick={handleClose}
        className="signup-close signup-close--left"
        aria-label="Volver"
        title="Volver"
      >
        <X size={18} aria-hidden="true" />
      </a>

      <header className="dd-header dd-header--simple"></header>

      <div className="dd-hero page-enter">
        <aside className="dd-left">
          <div className="dd-figure">
            <img src={perro} alt="Dog hero" loading="lazy" />
          </div>
        </aside>

        <section className="dd-right">
          <div className="dd-copy">
            <h1>Vamos a registrarte en PETFAM</h1>
            <p>Ingresa tu información a continuación</p>

            {influencerCode ? (
              <p
                style={{
                  marginTop: "8px",
                  marginBottom: 0,
                  fontSize: "13px",
                  color: "#0B2F3E",
                  fontWeight: 600,
                }}
              >
                Estás ingresando con el código: {influencerCode}
              </p>
            ) : null}
          </div>

          <div className="dd-card">
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "7px 12px",
                marginBottom: 12,
                borderRadius: 999,
                background: "rgba(0,149,169,.10)",
                color: "#0B2F3E",
                fontSize: 12,
                fontWeight: 800,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#0095A9",
                  boxShadow: "0 0 0 4px rgba(0,149,169,.13)",
                }}
              />
              Paso 1 de 2
            </div>

            <h2>Los campos con * son obligatorios</h2>

            {submitError ? (
              <p className="error-msg" style={{ marginTop: 8 }}>
                {submitError}
              </p>
            ) : null}

            <form onSubmit={(e) => e.preventDefault()} className="dd-form">
              <label>
                <span>Nombre completo*</span>
                <input
                  type="text"
                  placeholder="Tu nombre y apellido"
                  id="nombreCompleto"
                  name="nombreCompleto"
                  value={formData.nombreCompleto}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={loading || showSubmitting}
                  autoComplete="name"
                />
                {showError("nombreCompleto") ? (
                  <p className="error-msg">{errors.nombreCompleto}</p>
                ) : null}
              </label>

              <label>
                <span>Email*</span>
                <input
                  type="email"
                  placeholder="correo@ejemplo.com"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={loading || showSubmitting}
                  autoComplete="email"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                />

                {emailSuggestion && touched.email ? (
                  <p
                    style={{
                      marginTop: "6px",
                      marginBottom: 0,
                      fontSize: "12px",
                      lineHeight: 1.4,
                      color: "#0B2F3E",
                    }}
                  >
                    ¿Quisiste decir{" "}
                    <button
                      type="button"
                      onClick={applyEmailSuggestion}
                      disabled={loading || showSubmitting}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        margin: 0,
                        color: "inherit",
                        textDecoration: "underline",
                        cursor: "pointer",
                        font: "inherit",
                      }}
                    >
                      {emailSuggestion}
                    </button>
                    ?
                  </p>
                ) : null}

                {showError("email") ? <p className="error-msg">{errors.email}</p> : null}
              </label>

              <label>
                <span>Celular*</span>
                <input
                  type="tel"
                  placeholder="Tu celular"
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                  disabled={loading || showSubmitting}
                  autoComplete="tel"
                  inputMode="numeric"
                />
                {showError("telefono") ? <p className="error-msg">{errors.telefono}</p> : null}
              </label>

              <label>
                <span>Contraseña*</span>
                <div className="pw-field" style={{ position: "relative", display: "flex", alignItems: "center" }}>
                  <input
                    type={showPass ? "text" : "password"}
                    placeholder={`Mínimo ${MIN_PASSWORD_LENGTH} caracteres`}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    disabled={loading || showSubmitting}
                    autoComplete="new-password"
                    style={{ width: "100%", paddingRight: 44 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((v) => !v)}
                    aria-label={showPass ? "Ocultar contraseña" : "Ver contraseña"}
                    disabled={loading || showSubmitting}
                    style={{
                      position: "absolute",
                      right: 12,
                      border: "none",
                      background: "transparent",
                      display: "grid",
                      placeItems: "center",
                      cursor: "pointer",
                      color: "rgba(11,47,62,.75)",
                      padding: 0,
                    }}
                  >
                    {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {showError("password") ? <p className="error-msg">{errors.password}</p> : null}
              </label>

              <label>
                <span>Confirmar contraseña*</span>
                <div className="pw-field" style={{ position: "relative", display: "flex", alignItems: "center" }}>
                  <input
                    type={showConfirmPass ? "text" : "password"}
                    placeholder="Repite tu contraseña"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    required
                    disabled={loading || showSubmitting}
                    autoComplete="new-password"
                    style={{ width: "100%", paddingRight: 44 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPass((v) => !v)}
                    aria-label={showConfirmPass ? "Ocultar confirmación" : "Ver confirmación"}
                    disabled={loading || showSubmitting}
                    style={{
                      position: "absolute",
                      right: 12,
                      border: "none",
                      background: "transparent",
                      display: "grid",
                      placeItems: "center",
                      cursor: "pointer",
                      color: "rgba(11,47,62,.75)",
                      padding: 0,
                    }}
                  >
                    {showConfirmPass ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {showError("confirmPassword") ? (
                  <p className="error-msg">{errors.confirmPassword}</p>
                ) : null}
              </label>

              <button
                type="button"
                className="pill dark"
                onClick={handleRegistrarme}
                disabled={loading || showSubmitting}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                }}
              >
                Registrarme
              </button>

              <p className="dd-alt">
                ¿Ya tienes cuenta?{" "}
                <Link to="/login" state={{ from: "/crear-cuenta" }}>
                  Inicia sesión
                </Link>
              </p>
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}